import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {ChevronRight} from 'lucide-react';
import {Button, INK} from '../onboarding/review/reviewStyles';
import {DecisionBar} from '../onboarding/review/contractReviewStyles';
import {overviewApi, type Approval, type ProgramWorkspace} from './forwardFlowOverviewApi';
import {useViewer} from './ForwardFlowShared';

const Dock = styled(DecisionBar)`
  position: fixed; left: 50%; bottom: 18px; transform: translateX(-50%);
  width: min(1100px, calc(100vw - 380px)); box-sizing: border-box; z-index: 50; margin: 0;
  details { position: relative; } summary { cursor: pointer; color: ${INK.accentSoft}; font-size: 12px; margin-top: 3px; }
  ul { list-style: none; margin: 8px 0 0; padding: 0; max-height: 220px; overflow-y: auto; }
  li { display: flex; justify-content: space-between; gap: 12px; align-items: center; padding: 7px 0; border-top: 1px solid ${INK.line}; font-size: 13px; }
  li small { display: block; color: ${INK.muted}; font-size: 12px; }
  li button { border: 0; background: transparent; color: ${INK.accentSoft}; cursor: pointer; font: inherit; font-size: 12px; white-space: nowrap; }
`;

/**
 * Everything waiting on the reader as the second pair of eyes — agreements, purchases,
 * pre-clearance, bank confirmations, capacity records, stops, program changes, valuations
 * and servicing sign-off — with the way straight to each. Shown to checkers only, and only
 * for work they may take: their own requests are left to someone else.
 */
export function ForwardFlowApprovalsDock({programId, approvals, onOpen, current}: {
  programId?: number; approvals?: Approval[]; onOpen?: (workspace: ProgramWorkspace, query?: string | null) => void; current?: string;
}) {
  const viewer = useViewer();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState<Approval[]>([]);
  const load = useCallback(async () => {
    if (approvals || !viewer.checker) return;
    try { setLoaded(await overviewApi.approvals()); } catch { setLoaded([]); }
  }, [approvals, viewer.checker]);
  useEffect(() => {
    void load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);

  if (!viewer.checker) return null;
  const mine = (approvals ?? loaded).filter(a => a.actorMayAct);
  if (!mine.length) return null;
  const go = (a: Approval) => {
    if (programId === a.programId && onOpen) onOpen(a.workspace, a.query);
    else navigate(a.route);
  };
  const first = mine[0];
  // On the page where the first item is handled, the page's own decision bar carries the action.
  if (current && mine.length === 1 && current.startsWith(`${first.workspace}?`) && (!first.query || current.includes(first.query))) return null;

  return <Dock role="status" aria-label="Waiting for your approval">
    <div><strong>{mine.length} {mine.length === 1 ? 'item is' : 'items are'} waiting for you</strong>
      <span>{first.action} · {programId ? '' : `${first.programName} · `}{first.detail}</span>
      {mine.length > 1 && <details><summary>Show all {mine.length}</summary>
        <ul>{mine.map(a => <li key={a.key}><div>{a.action}<small>{programId ? '' : `${a.programName} · `}{a.detail} · prepared by {a.preparedBy}</small></div>
          <button type="button" onClick={() => go(a)}>Open →</button></li>)}</ul></details>}
    </div>
    <Button $variant="primary" type="button" onClick={() => go(first)}>{first.action} <ChevronRight size={14} /></Button>
  </Dock>;
}

/** The name other ABF pages already import. */
export const ForwardFlowCheckerBar = ForwardFlowApprovalsDock;
